import {
  object as yupObject,
  string as yupString,
} from 'yup';
import { checkIfScenarioNameExists } from './api';
import { ScenarioDataSources } from '../../shared/queries/scenarios';

const ERROR_MESSAGES = {
  required: fieldName => () => `${fieldName} is required`,
  oneOf: fieldName => ({ values }) => `${fieldName} must be one of ${values}`,
  max: fieldName => ({ max }) => `${fieldName} cannot be longer than ${max} characters`,
  oneOfExisting: (fieldName, collectionName) => () => `${fieldName} must be one of the existing ${collectionName}`,
  unique: fieldName => () => `${fieldName} must be unique`,
};

export const baseScenarioSchema = (scenario, { scenarios }) => yupObject().shape({
  scenarioName: yupString()
    .max(30, ERROR_MESSAGES.max('Scenario Name'))
    .required(ERROR_MESSAGES.required('Scenario Name')),
  periodName: yupString()
    .required(ERROR_MESSAGES.required('Period Name')),
  dataSource: yupString()
    .required(ERROR_MESSAGES.required('Data Source'))
    .oneOf(Object.values(ScenarioDataSources), ERROR_MESSAGES.oneOf('Data Source')),
  sourceName: yupString()
    .when('dataSource', {
      is: ScenarioDataSources.Copy,
      then: yupString()
        .oneOf(
          scenarios.filter(({ periodName }) => scenario.periodName === periodName)
            .map(({ scenarioName }) => scenarioName),
          ERROR_MESSAGES.oneOfExisting('Data Source', 'scenarios')
        )
        .required(ERROR_MESSAGES.required('Source Name is required for Data Source set to COPY')),
      otherwise: yupString()
        .nullable(),
    }),
  publicFlag: yupString().oneOf(['Y', 'N']),
  lockedFlag: yupString().oneOf(['Y', 'N']),
});

export const scenarioNameSchema = yupObject().shape({
  scenarioName: yupString()
    .test(
      'scenario-name-uniq',
      '${originalValue} is already used', // eslint-disable-line no-template-curly-in-string
      async (scenarioName, { parent: scenario }) => {
        if (!scenarioName) {
          return true;
        }
        const result = await checkIfScenarioNameExists({
          scenarioName,
          excludeScenarioId: scenario.meta.isAdded ? null : scenario.scenarioId,
        });
        return result.isUniq;
      }
    ),
});

const parseErrors = error => error.inner.reduce((acc, { errors, params }) => {
  acc[params.path] = errors;
  return acc;
}, {});

export const validateScenario = (scenario, context, scenarios) => {
  try {
    baseScenarioSchema(scenario, { scenarios }).validateSync(scenario, {
      abortEarly: false,
      strict: true,
    });
    return {};
  } catch (error) {
    return {
      errors: parseErrors(error),
    };
  }
};

export const asyncValidateScenario = async scenario => {
  try {
    await scenarioNameSchema.validate(scenario, {
      abortEarly: false,
      strict: true,
    });
    return {};
  } catch (error) {
    return {
      errors: parseErrors(error),
    };
  }
};
